import { Component, computed, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'truncate-span',
    standalone: true,
    imports: [NgbTooltip, TranslateModule],
    template: `
        @if (isTruncated()) {
            <span
                    [ngbTooltip]="text()"
                    [openDelay]="openDelay"
                    [container]="container"
                    class="truncated-text text-hover-container"
                    [style.min-width]="minWidth"
                    [attr.aria-label]="text()">
                <span [attr.data-test]="dataTest">{{ truncatedText() }}</span>
                @if (showCopyButton) {
                    <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary copy-btn copy-btn-hover"
                            (click)="copyToClipboard($event)"
                            [ngbTooltip]="copyTooltip | translate" [openDelay]="800"
                            [attr.aria-label]="'Copy'">
                    <i class="fa fa-copy" [class.fa-check]="showCopiedFeedback"></i>
                </button>
                }
            </span>
        } @else {
            <span
                    class="truncated-text-unwrapped text-hover-container">
                <span [attr.data-test]="dataTest">{{ text() }}</span>
                @if (showCopyButton && text()) {
                    <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary copy-btn copy-btn-hover"
                            (click)="copyToClipboard($event)"
                            [ngbTooltip]="copyTooltip  | translate" [openDelay]="800"
                            [attr.aria-label]="'Copy'">
                    <i class="fa fa-copy" [class.fa-check]="showCopiedFeedback"></i>
                </button>
                }
            </span>
        }
    `,
    styleUrl: './truncate-span.component.scss'
})
export class TruncateSpanComponent implements OnChanges {
    private readonly TRUNCATE_LENGTH_DEFAULT = 50;
    private readonly ELLIPSIS_DEFAULT = '...';

    // Inputs
    @Input() set textInput(value: string | number | null | undefined | any) {
        this.text.set(String(value || ''));
    }

    @Input() maxLength: number = this.TRUNCATE_LENGTH_DEFAULT;
    @Input() suffix: string = this.ELLIPSIS_DEFAULT;
    @Input() openDelay: number = 800;
    @Input() container: string = null;
    @Input() trimWhitespace: boolean = true;
    @Input() minWidth: string = '280px'; // New input for maximum width
    @Input() showCopyButton: boolean = true; // New input to show/hide copy button
    @Input('data-test') dataTest!: string;
    @Input() innerClass: string = '';


    // Signals for reactive state management
    protected text = signal<string>('');
    protected showCopiedFeedback = false;
    protected copyTooltip = 'global.copy';


    // Computed values that automatically update when dependencies change
    protected processedText = computed(() => {
        const rawText = this.text();
        return this.trimWhitespace ? rawText.trim() : rawText;
    });

    protected isTruncated = computed(() => {
        const processed = this.processedText();
        return processed.length > this.maxLength;
    });

    protected truncatedText = computed(() => {
        const processed = this.processedText();
        if (!this.isTruncated()) {
            return processed;
        }

        // Ensure we don't truncate to a length that would be longer with suffix
        const maxContentLength = Math.max(0, this.maxLength - this.suffix.length);
        return processed.substring(0, maxContentLength) + this.suffix;
    });


    ngOnChanges(changes: SimpleChanges): void {
        // Handle the legacy 'text' input for backward compatibility
        if (changes['text'] && changes['text'].currentValue !== undefined) {
            this.text.set(changes['text'].currentValue || '');
        }

        // Validate maxLength
        if (changes['maxLength']) {
            const newMaxLength = changes['maxLength'].currentValue;
            if (newMaxLength < 1) {
                console.warn('TruncateSpanComponent: maxLength must be greater than 0, using default');
                this.maxLength = this.TRUNCATE_LENGTH_DEFAULT;
            }
        }
    }

    protected copyToClipboard(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        const textToCopy = this.processedText();

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(textToCopy).then(() => {
                this.showCopyFeedback();
            }).catch(() => {
                this.fallbackCopyToClipboard(textToCopy);
            });
        } else {
            this.fallbackCopyToClipboard(textToCopy);
        }
    }

    private fallbackCopyToClipboard(text: string): void {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy');
            this.showCopyFeedback();
        } catch (err) {
            console.error('Failed to copy text: ', err);
        } finally {
            document.body.removeChild(textArea);
        }
    }

    private showCopyFeedback(): void {
        this.showCopiedFeedback = true;
        this.copyTooltip = 'global.copied';

        setTimeout(() => {
            this.showCopiedFeedback = false;
            this.copyTooltip = 'global.copy';
        }, 2000);
    }

}
